import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Activity } from 'lucide-react';

export default function AdminActivityPage() {
    const { apiCall } = useAdminAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const params = filter !== 'all' ? `?action=${filter}` : '';
                const res = await apiCall('get', `/admin/activity-logs${params}`);
                setLogs(res.data);
            } catch (e) {
                console.error(e);
                toast.error('No se pudo cargar la actividad');
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [apiCall, filter]);

    const actionColors = {
        admin_login: 'bg-sky-500/10 text-sky-500',
        create_tenant: 'bg-emerald-500/10 text-emerald-500',
        update_tenant: 'bg-amber-500/10 text-amber-500',
        tenant_suspended: 'bg-red-500/10 text-red-500',
        tenant_active: 'bg-emerald-500/10 text-emerald-500',
        change_plan: 'bg-violet-500/10 text-violet-500',
        create_lead: 'bg-orange-500/10 text-orange-500',
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="admin-activity-title">
                        Registro de Actividad
                    </h1>
                    <p className="text-muted-foreground mt-1">Historial de acciones de la plataforma</p>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="activity-filter">
                        <SelectValue placeholder="Filtrar por acción" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="admin_login">Login Admin</SelectItem>
                        <SelectItem value="create_tenant">Crear Tenant</SelectItem>
                        <SelectItem value="update_tenant">Actualizar Tenant</SelectItem>
                        <SelectItem value="change_plan">Cambio de Plan</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
            ) : (
                <Card className="card-aviation">
                    <CardContent className="p-0">
                        <div className="divide-y divide-border/30">
                            {logs.map((log, i) => (
                                <div key={i} className="flex items-start gap-4 p-4 hover:bg-muted/20 transition-colors" data-testid={`activity-row-${i}`}>
                                    <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium">{log.user_email}</span>
                                            <Badge className={actionColors[log.action] || 'bg-muted text-muted-foreground'} variant="secondary">
                                                {log.action}
                                            </Badge>
                                            {log.entity_type && (
                                                <span className="text-xs text-muted-foreground">
                                                    {log.entity_type}: {log.entity_id?.slice(0, 8)}...
                                                </span>
                                            )}
                                        </div>
                                        {log.details && Object.keys(log.details).length > 0 && (
                                            <p className="text-xs text-muted-foreground mt-1 font-mono">
                                                {JSON.stringify(log.details).slice(0, 120)}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {new Date(log.created_at).toLocaleString('es-ES')}
                                            {log.ip_address && <span className="ml-2 font-mono">{log.ip_address}</span>}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {logs.length === 0 && (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Sin registros de actividad</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
